import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { showRewardedAd } from "../lib/ads";

/**
 * "Double Up" rewarded-ad CTA shown on the win screen. Wraps the chips_200
 * server reward (1/day cap). Renders a compact button when idle and an
 * overlay during ad playback. On success calls `onCredited(chips)` so the
 * caller can refresh the displayed balance.
 *
 * Hides itself once the daily quota is exhausted (returns null after the
 * server says 429).
 */
export default function DoubleUpAdButton({
  onCredited,
  label = "Watch ad — claim +200 chips",
  rewardId = "chips_200",
  stake = 0,
}) {
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(null); // null | playing | err
  const [err, setErr] = useState("");
  const [hidden, setHidden] = useState(false);

  if (hidden) return null;

  const watch = async () => {
    if (busy) return;
    setBusy(true); setPhase("playing"); setErr("");
    try {
      const extra = rewardId === "chips_double_down" ? { stake } : {};
      const res = await showRewardedAd(rewardId, extra);
      onCredited?.(res.chips || 0);
      setPhase(null);
      setHidden(true); // collapse after successful claim
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Ad unavailable. Try again later.";
      if (status === 429) {
        setHidden(true); // already claimed today / cooldown — quietly hide
      } else {
        setErr(detail);
        setPhase("err");
        setTimeout(() => setPhase(null), 2200);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        onClick={watch}
        disabled={busy}
        className="rounded-full px-5 py-2.5 flex items-center justify-center gap-2 mx-auto mb-4 transition-all"
        style={{
          background: "linear-gradient(135deg, rgba(61,220,151,0.20), rgba(255,215,0,0.18))",
          border: "1.5px solid rgba(61,220,151,0.6)",
          boxShadow: "0 0 22px rgba(61,220,151,0.30)",
          opacity: busy ? 0.6 : 1,
          cursor: busy ? "wait" : "pointer",
        }}
        data-testid="double-up-ad-btn"
      >
        <span style={{ fontSize: 14 }}>📺</span>
        <span className="font-display font-bold text-sm tracking-wide">{label}</span>
      </motion.button>

      <AnimatePresence>
        {phase === "playing" && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center px-4"
            style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(8px)" }}
            data-testid="double-up-ad-overlay"
          >
            <div className="text-center">
              <div className="label-eyebrow mb-2" style={{ color: "#3DDC97", letterSpacing: 2 }}>
                SPONSORED · ~30s
              </div>
              <div className="font-display font-black text-3xl mb-2">Doubling your win…</div>
              <div className="text-white/55 text-xs">+200 chips when complete</div>
              <div className="mt-6 flex justify-center gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                    className="rounded-full"
                    style={{ width: 8, height: 8, background: "#3DDC97" }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
        {phase === "err" && err && (
          <motion.div
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            className="fixed left-1/2 z-[130] glass rounded-full px-5 py-3 text-xs"
            style={{ top: 24, transform: "translateX(-50%)", border: "1px solid rgba(255,92,92,0.5)", color: "#FF8A8A" }}
          >{err}</motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
